import { logger } from '@/utils/logger';
import { useAnalytics } from '@/context/analytics-context';
import useCategories from '@/hooks/use-categories';
import appServices from '@/services/appServices';
import { useInfiniteTransactions } from '@/hooks/use-infinite-transactions';
import { Query } from '@polybank/shared';
import { AppCategory, AppRule } from '@polybank/shared';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import useAppwriteTableQuery from './use-appwrite-table';
import useCurrentReport from '@/components/reports/useCurrentReport';
import { trackOperation } from '@/lib/operations/operation-manager';

export default function useRules() {
  const { trackEvent } = useAnalytics();
  const {
    data: rules,
    isLoading: loading,
    error,
    removeItem,
    addItem: addRule,
    refetch,
    addOrUpdateItem: updateRule,
    mappedRows,
  } = useAppwriteTableQuery('rules', [
    Query.orderAsc('order'),
    Query.orderAsc('$createdAt'),
    Query.select([
      '$id',
      'title',
      'description',
      'active',
      'order',
      'strict',
      'triggers.*',
      'actions.*',
      'humanReadable',
      'raw',
      'ruleGroups.*',
    ]),
    Query.limit(1000),
  ]);
  const { addItem: addCategory } = useCategories();
  const { invalidate: invalidateCurrentReport } = useCurrentReport();
  const { refetch: refetchTransactions } = useInfiniteTransactions();
  const executeMutation = useMutation({
    mutationFn: async ({
      ruleId,
      forceApply,
    }: {
      ruleId: string;
      forceApply: boolean;
    }) => {
      logger.debug('Executing rule:', ruleId);
      const result = await trackOperation(
        `rule:${ruleId}`,
        appServices.rulesApi.executeRule(ruleId, forceApply),
        'Executing rule'
      );
      logger.debug('Rule execution result:', result);
      return result;
    },
    onSuccess: (result) => {
      if (result?.success) {
        const data = result.data;
        trackEvent('rule_executed', {
          transactionsUpdated: data?.transactionsUpdated || 0,
        });
        toast.success(
          `Rule applied successfully! Updated ${data?.transactionsUpdated || 0} transactions in ${data?.processingTimeMs || 0}ms`
        );
      } else {
        toast.error('Failed to execute rule');
      }
    },
    onError: (error) => {
      logger.error('Failed to execute rule:', error);
      toast.error('Failed to execute rule');
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({
      rule,
      newCategory,
    }: {
      rule: AppRule;
      newCategory: AppCategory | undefined;
      applyNow: boolean;
      forceApply: boolean;
      regenerateHumanReadable: boolean; // kept for backward compatibility, now unused
    }) => {
      return appServices.rules.saveRuleWithCategory(rule, newCategory);
    },
    onSuccess: async (result, requestParams) => {
      const { rule, newCategory } = result;
      const { applyNow, forceApply } = requestParams;
      trackEvent('rule_created', { hasAiOrigin: false });
      updateRule(result?.rule);

      if (newCategory) {
        addCategory(newCategory);
      }

      // Execute the rule immediately if "apply now" is checked
      if (applyNow && rule.$id) {
        logger.debug('Applying rule immediately:', rule.$id);
        await executeMutation.mutateAsync({ ruleId: rule.$id, forceApply });
      }
      invalidateCurrentReport();
      refetchTransactions();
    },
    onError: (error) => {
      logger.error('Failed to create rule:', error);
      toast.error('Failed to create rule');
    },
  });

  return {
    rules,
    loading,
    error,
    removeItem,
    addItem: addRule,
    refetch,
    executeRule: executeMutation.mutateAsync,
    updateItem: updateRule,
    createNewRule: saveMutation.mutateAsync,
    isSavingRule: saveMutation.isPending,
    rulesMap: mappedRows,
  };
}
